import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { translations } from '../i18n/translations';
import { seoTranslations } from '../i18n/seoTranslations';
import { pagesTranslations } from '../i18n/pagesTranslations';
import { SITE_SLUGS, parsePath } from '../utils/siteNav';

// Deep merge SEO + page translations into main translations
[seoTranslations, pagesTranslations].forEach((extra) => Object.keys(extra).forEach(lang => {
  if (translations[lang]) {
    Object.keys(extra[lang]).forEach(section => {
      translations[lang][section] = extra[lang][section];
    });
  }
}));

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const VALID_LANGS = ['en', 'me', 'sq', 'de', 'pl', 'ru', 'hu', 'ro'];

// "/de/rooms" → site page in de; "/rooms" → site page in en; "/admin" → not a site page (null)
const siteLanguage = (pathname) => {
  const { lang, slug } = parsePath(pathname);
  const isSite = slug === '' ? (pathname === '/' || lang !== 'en') : SITE_SLUGS.includes(slug);
  return isSite ? lang : null;
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [language, setLanguageState] = useState(() => {
    // Public pages: the URL is the only source of truth (no stored-preference redirect — it broke crawler rendering and Google advises against it).
    // Language-agnostic tools (admin, guest links): last chosen language, default 'en'.
    const fromPath = siteLanguage(window.location.pathname);
    if (fromPath) return fromPath;
    const saved = localStorage.getItem('umapremium_language');
    if (saved && VALID_LANGS.includes(saved)) return saved;
    return 'en';
  });

  useEffect(() => {
    const lang = siteLanguage(location.pathname);
    if (lang) setLanguageState((cur) => (lang !== cur ? lang : cur));
  }, [location.pathname]);

  const setLanguage = (newLang) => {
    if (!VALID_LANGS.includes(newLang)) return;
    setLanguageState(newLang);
    
    // Update URL path (only on public pages — admin and guest tools are language-agnostic)
    const currentPath = location.pathname;
    const pathMatch = currentPath.match(/^\/([a-z]{2})(\/.*)?$/);
    const isLangPath = !!(pathMatch && VALID_LANGS.includes(pathMatch[1]));
    const seg = currentPath.split('/').filter(Boolean)[0] || '';
    if (!isLangPath && currentPath !== '/' && !SITE_SLUGS.includes(seg)) return;
    
    let newPath;
    if (newLang === 'en') {
      // English uses root path
      newPath = isLangPath ? (pathMatch[2] || '/') : currentPath;
    } else {
      // Other languages use /{lang}/ prefix
      newPath = isLangPath ? `/${newLang}${pathMatch[2] || ''}` : `/${newLang}${currentPath === '/' ? '' : currentPath}`;
    }
    
    navigate(newPath.replace(/\/{2,}/g, '/') + location.search + location.hash, { replace: true });
  };

  useEffect(() => {
    localStorage.setItem('umapremium_language', language);
    document.documentElement.lang = language === 'me' ? 'sr' : language;
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        // Fallback to English
        let fallback = translations['en'];
        for (const fk of keys) {
          if (fallback && typeof fallback === 'object') {
            fallback = fallback[fk];
          } else {
            return key;
          }
        }
        return fallback || key;
      }
    }
    
    return value || key;
  };

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧', path: '/' },
    { code: 'me', name: 'Crnogorski', flag: '🇲🇪', path: '/me/' },
    { code: 'sq', name: 'Shqip', flag: '🇦🇱', path: '/sq/' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪', path: '/de/' },
    { code: 'pl', name: 'Polski', flag: '🇵🇱', path: '/pl/' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺', path: '/ru/' },
    { code: 'hu', name: 'Magyar', flag: '🇭🇺', path: '/hu/' },
    { code: 'ro', name: 'Română', flag: '🇷🇴', path: '/ro/' }
  ];

  const currentLanguage = languages.find(l => l.code === language);

  // Memoize the context value so consumers don't re-render on every parent tick
  const value = useMemo(
    () => ({ language, setLanguage, t, languages, currentLanguage }),
    [language, currentLanguage],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
